import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import { useAuth } from "../hooks/useAuth";
import { useRouter } from "../hooks/useRouter";
import { useWallet } from "../hooks/useWallet";
import React from "react";
import { OPAAvatar } from "./OPAAvatar";
import { ChatChannelType } from "../models/ChatChannel";
import { useStream } from "../hooks/useStream";
import { Pleb, PlebData } from "../models/Pleb";

export const SidebarMenu = () => {
    const navigate = useNavigate();
    const { isDarkMode, setIsDarkMode } = useTheme();
    const {pleb, users, logout, setCurrentUserId} = useAuth();
    const { nbUnread } = useStream();
    const {navigateToProfile} = useRouter();
    const {nbTxUnread} = useWallet();
    const {isMobile} = useTheme();
    const location = useLocation();

    const addAccount = () => {
        navigate('/login', {
          state: {
              addAccount: true
          }
      });
    };

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    const handleProfileClick = (user: PlebData) => {
        if (user?.id) {
          let isCurrentUser = user.id === pleb?.id;

          if(isCurrentUser) {
            navigateToProfile(ChatChannelType.Messaging, user.id);
          } else {
            setCurrentUserId(user.id);
          }
        }
    };

    const menus = [
        {
            icon: <i className="material-symbols-outlined">chat</i>,
            label: 'Chat',
            path: '/home/chat',
            badge: nbUnread > 0 ? nbUnread : undefined
        },
        {
            icon: <i className="material-symbols-outlined">search</i>,
            label: 'Search',
            path: '/home/search',
        },
        {
            icon: <i className="material-symbols-outlined">account_balance_wallet</i>,
            label: 'Wallet',
            path: '/home/wallet',
            badge: nbTxUnread > 0 ? nbTxUnread : undefined
        },
    ];

    return (
        <div className={`
            flex-shrink-0 flex-grow-1 flex flex-col justify-between bg-brand-bg-1 p-4 gap-4 overflow-y-auto overflow-x-hidden 
            ${isMobile ? 'items-stretch w-full flex-grow' : 'w-[100px] items-center'}
          `}>
            <div className={`
                flex flex-col justify-center gap-4
                ${isMobile ? 'items-stretch flex-grow-1' : 'items-center'}
            `}>
              <div className="w-[50px] h-[50px] mt-2 self-center">
                <img 
                  src="/images/logo192.png"
                  alt="Logo"
                  className=""
                />
              </div>
              {menus.map((menu) => (
                <MenuItem 
                  key={menu.path}
                  icon={menu.icon}
                  label={menu.label}
                  onClick={() => {navigate(menu.path)}}
                  selected={location.pathname.startsWith(menu.path)}
                  badge={menu?.badge ? menu.badge.toString() : undefined}
                  isMobile={isMobile}
                />
              ))}
              <MenuItem
                icon={isDarkMode ? <i className="material-icons">light_mode</i> : <i className="material-icons">bedtime</i>}
                label="Mode"
                onClick={toggleTheme}
                selected={false}
                isMobile={isMobile}
              />
            </div>
            <div className="flex flex-col items-center justify-center gap-4">
              {
                Object.values(users).map((user) => {
                  let isCurrentUser = user.id === pleb?.id;

                  return (
                    <div onClick={() => {handleProfileClick(user)}} className="cursor-pointer" key={user.id}>
                      <OPAAvatar
                        imageUrl={user.image}
                        size={60}
                        borderWidth={4}
                        borderColor={isCurrentUser ? 'var(--brand-white)' : 'var(--brand-bg-1)'}
                      />
                    </div>
                  )
                })
              }
              <MenuItem
                label="Add Account"
                onClick={() => {addAccount()}}
                selected={false}
                isMobile={isMobile}
              />
              <MenuItem
                label="Logout"
                onClick={() => {logout()}}
                selected={false}
                isMobile={isMobile}
                classNameLabel="text-brand-red"
              />
            </div>
          </div>
    );
}

interface MenuItemProps {
  icon?: React.ReactNode,
  label: string,
  onClick: () => void,
  selected: boolean,
  badge?: string,
  isMobile: boolean,
  className?: string,
  classNameLabel?: string
}

const MenuItem = ({icon, label, onClick, selected, badge, isMobile, className, classNameLabel}: MenuItemProps) => {
    return (
      <div className={`
        flex  items-stretch justify-center relative
        ${isMobile ? 'flex-row' : 'flex-col'}
        ${!isMobile && !!icon && 'w-[70px] h-[70px]'}
        ${className}
      `}>
        <div 
          className={`
            flex-grow
            flex items-center cursor-pointer relative rounded-2xl
            ${selected ? 'text-brand-color-1 icon-menu-selected' : 'text-brand-color-2'}
            ${isMobile ? 'flex-row gap-2 py-2 px-4 justify-start' : 'flex-col justify-center'}
            hover:text-brand-color-1 gap-1
          `}
          onClick={onClick}
        >
          {icon}
          <span className={`font-semibold text-sm text-center ${classNameLabel}`}>{label}</span>
        </div>
        {badge && <div className="min-h-[24px] min-w-[24px] px-1.5 bg-brand-red rounded-full flex items-center justify-center text-white text-sm font-semibold absolute top-[-7px] right-[-7px]">{badge}</div>}
      </div>
    );
  }
